import {
  Box,
  Button,
  Input,
  Menu,
  MenuButton,
  MenuList,
  Text,
  HStack,
  Tooltip,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  MenuItem
} from '@chakra-ui/react';
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-light-svg-icons';
import { useQuery } from '@tanstack/react-query';
import { ButtonText, FormFeedback } from '@morressier/alembic';
import {
  faMagnifyingGlass,
  faPlus,
  faX
} from '@fortawesome/pro-regular-svg-icons';
import { toTitleCase } from './signupHook';

export type CustomSearchSelectOption = {
  value: string;
  label: string;
};

type CustomSearchSelectAsyncProps = {
  value: any;
  onChange: (fieldName: string, value: any) => void;
  isInvalid?: boolean;
  placeholder: string;
  searchMethod: (search: string, limit: number) => Promise<any>;
  fieldName: string;
  termSearch?: string;
  customDataEntry?: ReactElement;
  isDisabled?: boolean;
  errorMessage?: string;
  rowIndex: number;
  handleNewKeywordClick: (
    index: number,
    fieldName: string,
    fieldLabel?: string
  ) => void;
  newKeywordFieldName?: string;
  newKeywordLabel?: string;
  showNewKeyword?: boolean;
  isOptionDisabled?: (item: IListItem) => boolean;
};

type CustomSearchSelectStaticProps = {
  value: string;
  onChange: (value: string) => void;
  isInvalid?: boolean;
  placeholder: string;
  options: CustomSearchSelectOption[];
  isDisabled?: boolean;
  errorMessage?: string;
  isOptionDisabled?: (item: IListItem) => boolean;
};

type CustomSearchSelectProps =
  | CustomSearchSelectAsyncProps
  | CustomSearchSelectStaticProps;

type IListItem = {
  name: string;
  termId: string;
  value?: string;
  label: string;
};

export const CustomSearchSelect: React.FC<CustomSearchSelectProps> = props => {
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [search, setSearch] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuWidth, setMenuWidth] = useState<number | undefined>(undefined);
  const isStaticMode = 'options' in props;
  const isDependentAsyncMode = !isStaticMode && !!props.termSearch;

  const { value, isInvalid, placeholder, isDisabled, errorMessage } = props;

  // For dependent async selects (e.g., keywords), we fetch by parent id and
  // filter client-side using the current search text.
  const searchQuery = isStaticMode
    ? ''
    : isDependentAsyncMode
    ? props.termSearch || ''
    : search || ' ';

  const { data } = useQuery({
    queryKey: isStaticMode
      ? ['custom-search-select-static']
      : [props.fieldName, searchQuery],
    queryFn: () =>
      isStaticMode
        ? Promise.resolve({ data: [] })
        : props.searchMethod(searchQuery, 50),
    enabled: !isStaticMode && !!searchQuery
  });

  const listData = isStaticMode ? props.options : data?.data ?? [];

  const filteredListData = useMemo(() => {
    if (listData?.length === 0) return listData;

    const queryLower = search.trim().toLowerCase();
    if (!queryLower) return listData;

    return (
      listData &&
      listData?.filter((item: any) =>
        (isStaticMode ? item.label : item.name)
          ?.toLowerCase()
          .includes(queryLower)
      )
    );
  }, [isStaticMode, listData, search]);

  useEffect(() => {
    if (buttonRef.current) {
      const { width } = buttonRef.current.getBoundingClientRect();
      setMenuWidth(width);
    }
  }, []);

  const handleItemSelect = useCallback(
    (item: any) => {
      if (isStaticMode) {
        props.onChange(item.value);
      } else {
        props.onChange(props.fieldName, item);
      }
      setMenuOpen(false);
      setSearch('');
    },
    [isStaticMode, props]
  );

  const handleClose = () => {
    setMenuOpen(!menuOpen);
    setSearch('');
  };

  const selectedLabel = isStaticMode
    ? props.options.find(option => option.value === value)?.label
    : value?.name;
  const displayLabel = selectedLabel?.trim()
    ? selectedLabel
    : `Select a ${placeholder}`;

  const menuButton = (
    <MenuButton
      as={Button}
      variant="unstyled"
      rightIcon={<FontAwesomeIcon icon={faChevronDown} />}
      onClick={handleClose}
      width="100%"
      minWidth="0"
      textAlign="left"
      backgroundColor={isInvalid ? 'errorPrimaryFill' : 'inputFill'}
      border="1px solid"
      borderWidth="1px"
      borderStyle="solid"
      boxShadow="none"
      borderRadius="small"
      color="inputContent"
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      _focus={{
        boxShadow: 'none',
        bg: isInvalid ? 'errorPrimaryFill' : 'inputFill',
        borderColor: isInvalid ? 'errorPrimaryBorder' : 'inputBorder'
      }}
      _focusVisible={{
        layerStyle: 'focus',
        boxShadow: 'none',
        bg: isInvalid ? 'errorPrimaryFill' : 'inputFill',
        borderColor: isInvalid ? 'errorPrimaryBorder' : 'inputBorder'
      }}
      _hover={{
        bg: isInvalid ? 'errorPrimaryFill' : 'inputFill',
        borderColor: isInvalid ? 'errorPrimaryBorder' : 'inputBorderHover'
      }}
      _active={{
        bg: isInvalid ? 'errorPrimaryFill' : 'inputFill',
        borderColor: isInvalid ? 'errorPrimaryBorder' : 'inputBorderActive'
      }}
      _expanded={{
        bg: isInvalid ? 'errorPrimaryFill' : 'inputFill',
        borderColor: isInvalid ? 'errorPrimaryBorder' : 'inputBorderActive',
        boxShadow: 'none'
      }}
      cursor="pointer"
      ref={buttonRef}
      height="40px"
      py="8px"
      px={2}
      borderColor={isInvalid ? 'errorPrimaryBorder' : 'inputBorder'}
      disabled={isDisabled}
    >
      <HStack>
        <Text
          textStyle="textBody"
          isTruncated
          color={selectedLabel ? (isInvalid ? 'error' : 'inherit') : 'GrayText'}
        >
          {displayLabel}
        </Text>
      </HStack>
    </MenuButton>
  );

  return (
    <>
      <Menu isOpen={menuOpen} onClose={() => setMenuOpen(false)}>
        {selectedLabel ? (
          <Tooltip label={selectedLabel} placement="top" hasArrow>
            {menuButton}
          </Tooltip>
        ) : (
          menuButton
        )}
        <MenuList
          width='auto'
          minWidth={menuWidth ? `${menuWidth}px` : 'auto'}
          maxH="250px"
          overflowY="auto"
          overscrollBehavior="contain"
          zIndex="1007"
          p={0}
        >
          <Box
            position="sticky"
            top="0"
            bg="surfacePrimaryFill"
            zIndex="1"
            p={2}
          >
            <InputGroup size="md">
              <InputLeftElement pointerEvents="none">
                <FontAwesomeIcon icon={faMagnifyingGlass} />
              </InputLeftElement>
              <Input
                width="100%"
                minWidth="0"
                flex="1"
                border="1px solid"
                aria-label={`input-search-for-${placeholder}`}
                borderColor="neutralPrimaryBorder"
                placeholder={
                  isStaticMode
                    ? `Search ${placeholder.toLowerCase()}`
                    : `Search for ${placeholder}`
                }
                value={search}
                onChange={e => setSearch(e.target.value)}
                pl="2.2rem"
                autoFocus
              />
              {search && (
                <InputRightElement
                  onClick={() => setSearch('')}
                  cursor={'pointer'}
                >
                  <FontAwesomeIcon icon={faX} size="sm" />
                </InputRightElement>
              )}
            </InputGroup>
          </Box>

          {filteredListData?.length > 0 ? (
            filteredListData?.map((item: IListItem) => {
              const isOptionDisabled = props.isOptionDisabled?.(item) ?? false;

              return (
                <MenuItem
                  key={isStaticMode ? item.value : item.termId}
                  px={2}
                  py={1}
                  borderRadius="md"
                  _hover={
                    isOptionDisabled
                      ? undefined
                      : { bg: 'actionSecondaryFill', cursor: 'pointer' }
                  }
                  onClick={() => !isOptionDisabled && handleItemSelect(item)}
                  isDisabled={isOptionDisabled}
                >
                  <HStack py={1}>
                    <Text textStyle="textBody">
                      {isStaticMode ? item.label : item.name}
                    </Text>
                  </HStack>
                </MenuItem>
              );
            })
          ) : (
            <Box p={2} textAlign={'center'}>
              <Text color="GrayText">
                {isStaticMode ? 'No options found' : 'No data found'}
              </Text>
            </Box>
          )}

          {!isStaticMode && !props.showNewKeyword && (
            <Box
              position="sticky"
              bottom="0"
              bg="surfacePrimaryFill"
              py={2}
              borderTopWidth="1px"
              borderTopColor="gray.200"
              zIndex="1"
            >
              <MenuItem
                key="new-keyword"
                px={2}
                borderRadius="md"
                _hover={{
                  bg: 'actionSecondaryFill',
                  cursor: 'pointer'
                }}
                onClick={() => {
                  props.handleNewKeywordClick(
                    props.rowIndex,
                    props.newKeywordFieldName ?? placeholder,
                    props.newKeywordLabel ?? placeholder
                  );
                }}
              >
                <ButtonText iconLeft={<FontAwesomeIcon icon={faPlus} />}>
                  New {toTitleCase(props.newKeywordLabel ?? placeholder)}
                </ButtonText>
              </MenuItem>
            </Box>
          )}
        </MenuList>
      </Menu>
      {errorMessage && (
        <FormFeedback variant="error">{errorMessage}</FormFeedback>
      )}
    </>
  );
};
